import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { motion } from "framer-motion";
import { Check } from "lucide-react";

export function PricingSection() {
  const pricingPlans = [
    {
      name: "Starter",
      description: "Perfect for small businesses",
      features: [
        "Basic Digital Marketing",
        "Social Media Management",
        "Monthly Reports",
        "Email Support",
        "Basic Analytics"
      ],
      highlighted: false,
      color: "orange-500"
    },
    {
      name: "Growth",
      description: "Ideal for growing businesses",
      features: [
        "Full Digital Marketing Suite",
        "Offline Event Support",
        "A+ Content Creation",
        "Weekly Reports",
        "Priority Support",
        "Advanced Analytics",
        "KOL Network Access"
      ],
      highlighted: true,
      color: "orange-600"
    },
    {
      name: "Enterprise",
      description: "Comprehensive solution for large businesses",
      features: [
        "Custom Tech Solutions",
        "Dedicated Account Manager",
        "24/7 Support",
        "Real-time Analytics",
        "Custom Integration",
        "White-label Solutions",
        "Strategic Consultation"
      ],
      highlighted: false,
      color: "orange-700"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-orange-50 to-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Service Packages
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-orange-700 mx-auto mb-8"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Choose the service package that fits your business needs and scale
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {pricingPlans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
              className="h-full"
            >
              <Card className={`h-full relative flex flex-col ${
                plan.highlighted 
                  ? 'border-orange-600 border-2 shadow-2xl scale-105 bg-gradient-to-b from-orange-50 to-white' 
                  : 'shadow-lg hover:shadow-xl'
              } transition-all duration-300`}>
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-orange-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <CardHeader className="text-center pb-6">
                  <CardTitle className="text-2xl font-bold text-gray-900 mb-3">
                    {plan.name}
                  </CardTitle>
                  <p className="text-gray-600 text-lg">
                    {plan.description}
                  </p>
                </CardHeader>

                <CardContent className="px-6 pb-6 flex flex-col h-full">
                  <div className="flex-grow">
                    <ul className="space-y-4">
                      {plan.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center space-x-3">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                            plan.color === 'orange-600' ? 'bg-orange-100' :
                            plan.color === 'orange-700' ? 'bg-orange-100' : 'bg-orange-100'
                          }`}>
                            <Check className={`w-3 h-3 ${
                              plan.color === 'orange-600' ? 'text-orange-600' :
                              plan.color === 'orange-700' ? 'text-orange-700' : 'text-orange-500'
                            }`} />
                          </div>
                          <span className="text-gray-700 leading-relaxed">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-8 pt-2">
                    <Button 
                      className={`w-full ${
                        plan.highlighted 
                          ? 'bg-orange-600 hover:bg-orange-700' 
                          : plan.color === 'orange-700' 
                            ? 'bg-orange-700 hover:bg-orange-800'
                            : 'bg-orange-500 hover:bg-orange-600'
                      } transition-colors duration-300`}
                      size="lg"
                    >
                      Contact Us
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}